import { CdkDrag } from '@angular/cdk/drag-drop';
import { LOCATION_INITIALIZED } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, Injector, NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrowserModule, HAMMER_GESTURE_CONFIG, HammerGestureConfig, HammerModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { IconComponent } from '@components/icon/icon.component';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { TablesPlanEffects } from '@store/effects/tables-plan.effects';
import * as fromTablesPlan from '@store/reducers/tables-plan.reducer';
import { NgChartsModule } from 'ng2-charts';
import { MomentModule } from 'ngx-moment';
import { NgxSpinnerModule } from "ngx-spinner";
import { environment } from '../environments/environment';
import { RequestInterceptor } from './apis/request.interceptor';
import { ResponseInterceptor } from './apis/response.interceptor';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { LoginComponent } from './auth/login/login.component';
import { BlockingComponent } from './blocking/blocking.component';
import { CheckboxComponent } from './checkbox/checkbox.component';
import { ColorDropdownComponent } from './color-dropdown/color-dropdown.component';
import { CommunicationModalComponent } from './communication-modal/communication-modal.component';
import { CustomerTypesComponent } from './customer-tags/customer-tags.component';
import { DatepickerComponent } from './datepicker/datepicker.component';
import { DietaryTagModalComponent } from './dietary-tag-modal/dietary-tag-modal.component';
import { DietaryTagsComponent } from './dietary-tags/dietary-tags.component';
import { DottedDropdownComponent } from './dotted-dropdown/dotted-dropdown.component';
import { DropdownComponent } from './dropdown/dropdown.component';
import { EmployeeListComponent } from './employee-list/employee-list.component';
import { AutocompleteDirective } from './lib/auto-complete.directive';
import { CanReadDirective } from './lib/can-read.directive';
import { CanWriteDirective } from './lib/can-write.directive';
import { CapacitorNetworkService } from './lib/capacitor-network.service';
import { CapacitorStorageService } from './lib/capacitor-storage.service';
import { FilterPipe } from './lib/filter.pipe';
import { FormatPhonePipe } from './lib/format-phone.pipe';
import { GroupByPipe } from './lib/group-by.pipe';
import { HighlightSearchPipe } from './lib/highlight-search.pipe';
import { LocalStorageService } from './lib/local-storage.service';
import { SortPipe } from './lib/sort.pipe';
import { UppercasePipe } from './lib/uppercase.pipe';
import { ModalDialogComponent } from './modal-dialog/modal-dialog.component';
import { NumberpickerComponent } from './numberpicker/numberpicker.component';
import { SearchPipePipe } from './pipes/search-pipe.pipe';
import { ReportsComponent } from './reports/reports.component';
import { CompactReservationListItemComponent } from './reservation-list/compact-reservation-list-item/compact-reservation-list-item.component';
import { ReservationListItemComponent } from './reservation-list/reservation-list-item/reservation-list-item.component';
import { ReservationListComponent } from './reservation-list/reservation-list.component';
import { ReservationSearchComponent } from './reservation-search/reservation-search.component';
import { ReservationStatusModalComponent } from './reservation-status-modal/reservation-status-modal.component';
import { ReservationTagModalComponent } from './reservation-tag-modal/reservation-tag-modal.component';
import { ReservationTagsComponent } from './reservation-tags/reservation-tags.component';
import { ReservationComponent } from './reservation/reservation.component';
import { TagsModalComponent } from './reservation/tags-modal/tags-modal.component';
import { TagsComponent } from './reservation/tags/tags.component';
import { SourceModalComponent } from './source-modal/source-modal.component';
import { SourceComponent } from './source/source.component';
import { metaReducers, reducers } from './store';
import { AppEffects } from './store/effects/app.effects';
import { EmployeeEffects } from './store/effects/employee.effects';
import { ReservationListEffects } from './store/effects/reservation-list.effects';
import { ReservationEffects } from './store/effects/reservation.effects';
import * as fromApp from './store/reducers/app.reducer';
import * as fromEmployee from './store/reducers/employee.reducer';
import * as fromReservationList from './store/reducers/reservation-list.reducer';
import * as fromReservation from './store/reducers/reservation.reducer';
import { CanvasComponent } from './tables-plan/canvas/canvas.component';
import { LineComponent } from './tables-plan/canvas/line/line.component';
import { TableCoordinatesPipe } from './tables-plan/canvas/table/table-coordinates.pipe';
import { TableImagePipe } from './tables-plan/canvas/table/table-image.pipe';
import { TableSlotProgressPipe, TableSlotStylePipe, TableSlotTimePipe } from './tables-plan/canvas/table/table-slot.pipe';
import { TableComponent } from './tables-plan/canvas/table/table.component';
import { AllocatedTableNamesPipe, AllocatedTableNamesWithCategoryPipe, CoversByReservationTypePipe, ReservationsByTypeFilterPipe, UniqueCustomerTagsCategoriesPipe, UniqueReservationTagsCategoriesPipe } from './tables-plan/sidepanel/reservations-by-type-filter.pipe';
import { SidepanelComponent } from './tables-plan/sidepanel/sidepanel.component';
import { TablesPlanUseReservationComponent } from './tables-plan/sidepanel/tables-plan-use-reservation/tables-plan-use-reservation.component';
import { TablesPlanComponent } from './tables-plan/tables-plan.component';
import { ToolboxComponent } from './tables-plan/toolbox/toolbox.component';
import { TopbarComponent } from './tables-plan/topbar/topbar.component';
import { TagBaseComponent } from './tag-base/tag-base.component';
import { TagCategoryModalComponent } from './tag-category-modal/tag-category-modal.component';
import { TagIconComponent } from './tag-icon/tag-icon.component';
import { ShiftFromToHourPipe } from './pipes/shift-from-to-hour.pipe';
import { FilterByTableTypePipe, UniqueTableTypesPipe } from './tables-plan/toolbox/toolbox.pipe';
import { OrderDetailsComponent } from './order-details/order-details.component';
import { SettingsFormComponent } from './settings-form/settings-form.component';
import { FontSizeClassPipe } from './pipes/font-size-class.pipe';
import { CancelInterceptor } from './apis/cancel.interceptor';
import { UnknownSourcePipe } from './reservation-list/unknown-source.pipe';
import { SettingsComponent } from './tables-plan/settings/settings.component';
import { ReservationsExportComponent } from './reservation-export/reservations-export.component';
import { ToastService } from './lib/toast.service';
import { ToastComponent } from '@components/toast/toast.component';
import { TagsWrapPipe } from './reservation-list/reservation-list-item/tags-wrap.pipe';
import { ReservationsInfoPipe } from './tables-plan/topbar/reservations-info.pipe';

export function appInitializerFactory(translate: TranslateService, localStorageService: LocalStorageService, capacitorNetworkService: CapacitorNetworkService, injector: Injector) {
  return () => new Promise<any>((resolve: any) => {

    capacitorNetworkService.init();

    const locationInitialized = injector.get(LOCATION_INITIALIZED, Promise.resolve(null));
    locationInitialized.then(() => {
      const langToSet = localStorageService.getLanguage();

      // this language will be used as a fallback when a translation isn't found in the current language
      translate.setDefaultLang('en');

      // the lang to use, if the lang isn't available, it will use the current loader to get them
      translate.use(langToSet).subscribe(() => {
        console.info(`Successfully initialized '${langToSet}' language.'`);
      }, err => {
        console.error(`Problem with '${langToSet}' language initialization.'`);
      }, () => {
        resolve(null);
      });
    });
  });
}

class HammerConfig extends HammerGestureConfig {
  override overrides = <any>{
    swipe: { enable: true, direction: Hammer.DIRECTION_DOWN },
    pan: { enable: false },
    pinch: { enable: false },
    rotate: { enable: false },
  };
}

@NgModule({
  declarations: [
    AppComponent,
    DietaryTagsComponent,
    ReservationListComponent,
    ReservationComponent,
    DottedDropdownComponent,
    ModalDialogComponent,
    SearchPipePipe,
    FontSizeClassPipe,
    ReservationTagsComponent,
    ColorDropdownComponent,
    CustomerTypesComponent,
    SourceComponent,
    ReportsComponent,
    DropdownComponent,
    LoginComponent,
    DatepickerComponent,
    FilterPipe,
    SortPipe,
    NumberpickerComponent,
    HighlightSearchPipe,
    CanReadDirective,
    CanWriteDirective,
    AutocompleteDirective,
    BlockingComponent,
    ReservationSearchComponent,
    ReservationListItemComponent,
    SourceModalComponent,
    DietaryTagModalComponent,
    GroupByPipe,
    ReservationTagModalComponent,
    FormatPhonePipe,
    ReservationStatusModalComponent,
    CheckboxComponent,
    TagBaseComponent,
    TagCategoryModalComponent,
    TagsComponent,
    UppercasePipe,
    CommunicationModalComponent,
    CompactReservationListItemComponent,
    TagIconComponent,
    TagsModalComponent,
    EmployeeListComponent,
    TablesPlanComponent,
    SettingsComponent,
    ReservationsExportComponent,
    CanvasComponent,
    SidepanelComponent,
    ToolboxComponent,
    TopbarComponent,
    TableComponent,
    LineComponent,
    TableCoordinatesPipe,
    TableImagePipe,
    TableSlotStylePipe,
    TableSlotProgressPipe,
    TableSlotTimePipe,
    TablesPlanUseReservationComponent,
    IconComponent,
    ReservationsByTypeFilterPipe,
    CoversByReservationTypePipe,
    AllocatedTableNamesPipe,
    AllocatedTableNamesWithCategoryPipe,
    ShiftFromToHourPipe,
    UniqueCustomerTagsCategoriesPipe,
    UniqueReservationTagsCategoriesPipe,
    UniqueTableTypesPipe,
    FilterByTableTypePipe,
    OrderDetailsComponent,
    SettingsFormComponent,
    ToastComponent,
    UnknownSourcePipe,
    TagsWrapPipe,
    ReservationsInfoPipe,
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    HttpClientModule,
    FormsModule,
    NgChartsModule,
    NgxSpinnerModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: httpTranslateLoader,
        deps: [HttpClient]
      }
    }),
    MomentModule,
    HammerModule,
    CdkDrag,
    StoreModule.forRoot(reducers, { metaReducers }),
    !environment.production ? StoreDevtoolsModule.instrument() : [],
    StoreDevtoolsModule.instrument({ maxAge: 25, logOnly: environment.production }),
    EffectsModule.forRoot([]),
    EffectsModule.forFeature([AppEffects, EmployeeEffects, ReservationListEffects, ReservationEffects, TablesPlanEffects]),
    StoreModule.forFeature(fromApp.appFeatureKey, fromApp.reducer),
    StoreModule.forFeature(fromEmployee.employeeFeatureKey, fromEmployee.reducer),
    StoreModule.forFeature(fromReservationList.reservationListFeatureKey, fromReservationList.reducer),
    StoreModule.forFeature(fromReservation.reservationFeatureKey, fromReservation.reducer),
    StoreModule.forFeature(fromTablesPlan.tablesPlanFeatureKey, fromTablesPlan.reducer)
  ],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: RequestInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ResponseInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: CancelInterceptor, multi: true },
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFactory,
      deps: [TranslateService, LocalStorageService, CapacitorNetworkService, Injector],
      multi: true
    }, {
      provide: HAMMER_GESTURE_CONFIG,
      useClass: HammerConfig
    },
    CapacitorStorageService,
    CapacitorNetworkService,
    ToastService,
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppModule { }
export function httpTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http);
}
